import { useState } from "react";
import type { ApiResponse } from "../../../../../api";
import { useToast } from "../../../../../mantine/notifications/useToast";
import type { TodoFile } from "../../../../../utils/backend-types";
import { DropzoneWithContent } from "../../../../DropzoneWithContent/DropzoneWithContent";
import { useShouldShowStaffView } from "../../../../StaffViewToggle/useShouldShowStaffView";
import { WhichUserModal } from "../../WhichUserModal/WhichUserModal";

let filesToUpload: Array<File>;

interface DocumentsDropzoneProps {
  todoId: number;
  addTodoDocuments: (documentsInfo: {
    todoId: number;
    newFiles: Array<File>;
    asSystemUser: boolean;
  }) => Promise<Array<PromiseSettledResult<ApiResponse>>>;
}

function DocumentsDropzone({
  todoId,
  addTodoDocuments
}: DocumentsDropzoneProps) {
  const { showToast, showErrorToast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [showSystemUserQuestionModal, setShowSystemUserQuestionModal] =
    useState(false);
  const { shouldShowStaffView } = useShouldShowStaffView();

  function handleDrop(files: Array<File>) {
    if (shouldShowStaffView) {
      filesToUpload = files;
      setShowSystemUserQuestionModal(true);
    } else {
      handleUploadFiles(files, false);
    }
  }

  function handleUploadFiles(files: Array<File>, asSystemUser: boolean) {
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    addTodoDocuments({ todoId, newFiles: files, asSystemUser }).then(
      (results) => {
        const uploadedFileResponses: Array<TodoFile> = [];

        results.forEach((result) => {
          if (result.status === "fulfilled") {
            uploadedFileResponses.push(result.value.data);
          } else {
            showErrorToast(result.reason);
          }
        });

        const allSucceeded = results.length === uploadedFileResponses.length;
        const someSucceeded =
          results.filter((result) => result.status === "fulfilled").length > 0;

        if (allSucceeded) {
          showToast({
            type: "success",
            message: "Dateien wurden erfolgreich hochgeladen."
          });
        } else if (someSucceeded) {
          showToast({
            type: "success",
            message:
              "Einige Dateien konnten nicht hochgeladen. Bitte prüfen Sie welche Dateien nicht vorhanden sind und versuchen Sie es erneut."
          });
        }

        setIsUploading(false);
      }
    );
  }

  function handleChooseWhichUser(asSystemUser: boolean) {
    setShowSystemUserQuestionModal(false);
    handleUploadFiles(filesToUpload, asSystemUser);
  }

  return (
    <>
      <DropzoneWithContent
        loading={isUploading}
        textContent="Um neue Dokumente hochzuladen, können Sie diese hier per Drag&Drop ablegen oder klicken, um Dokumente auszuwählen."
        onDrop={handleDrop}
      />

      <WhichUserModal
        isOpen={showSystemUserQuestionModal}
        questionText="Wie sollen die Dateien hochgeladen werden?"
        toggle={() =>
          setShowSystemUserQuestionModal(!showSystemUserQuestionModal)
        }
        onClickSelf={() => handleChooseWhichUser(false)}
        onClickSystemUser={() => handleChooseWhichUser(true)}
      />
    </>
  );
}

export { DocumentsDropzone };
